'use client'

import { useEffect, useRef, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { ShoppingBag, Link as LinkIcon, Unlink, Pencil, Loader2, Eye, EyeOff, CheckCircle, ExternalLink, Trash2, RefreshCw, CalendarIcon, CreditCard } from 'lucide-react'
import { IntegrationCardLayout } from './integration-card-layout'
import { AddShopifyIntegrationDialog } from './add-shopify-integration-dialog'
import { useShopifyIntegration, type ShopifyIntegrationRecord } from '@/hooks/integrations/use-shopify-integration'
import { BILLING_CHANNELS } from '@/lib/billing/constants'
import { showBillingStoreControls } from '@/lib/shopify/billing-store-ui'
import { getShopifyAppListingUrl } from '@/lib/shopify/constants/app-listing-url'
import { usePermissions } from '@/hooks/use-permissions'
import { sanitizeDomain } from '@/lib/utils'
import { toast } from 'sonner'

export function ShopifyIntegrationCard() {
  const {
    integrations,
    requiredScopes,
    billingChannel,
    error,
    isLoading,
    isConnecting,
    disconnectingId,
    togglingId,
    updatingId,
    syncingId,
    syncingCatalogId,
    settingBillingStoreId,
    connectIntegration,
    startOAuthConnect,
    startCustomAppConnect,
    disconnectIntegration,
    toggleIntegration,
    setBillingStore,
    updateIntegration,
    syncOrders,
    syncCatalog,
    refresh,
    clearError,
    fetchIntegrationDetails,
  } = useShopifyIntegration()
  const { can } = usePermissions()
  const canDeleteData = can('integrations.shopify.delete_data')

  // Shopify-channel only; surfaced when there's a real choice (≥2 eligible) or a
  // recovery is needed (designated store broke + an eligible alternative exists).
  // Backend enforces the same gates; this only controls visibility. See
  // lib/shopify/billing-store-ui.ts.
  const billingControlsVisible = showBillingStoreControls(integrations, billingChannel)
  const shopifyListingUrl = getShopifyAppListingUrl()

  const [showConnectDialog, setShowConnectDialog] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null)
  const [showEditToken, setShowEditToken] = useState(false)
  const [editFormData, setEditFormData] = useState({
    shop_domain: '',
    uses_webhooks: false,
    uses_api: false,
    webhook_secret: '',
    api_access_token: '',
  })
  const [disconnectDialogIntegration, setDisconnectDialogIntegration] = useState<ShopifyIntegrationRecord | null>(null)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [syncPopoverOpen, setSyncPopoverOpen] = useState<string | null>(null)
  const [syncDate, setSyncDate] = useState<Date>(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 2); return d
  })
  const editingIdRef = useRef<string | null>(null)

  useEffect(() => {
    editingIdRef.current = editingId
  }, [editingId])

  const handleDisconnect = async (mode: 'soft' | 'hard') => {
    if (!disconnectDialogIntegration) return
    const { id, shop_domain } = disconnectDialogIntegration
    setDisconnectDialogIntegration(null)
    setDeleteConfirmInput('')

    const result = await disconnectIntegration(id, mode)
    if (result) {
      if (mode === 'hard') {
        const deletedOrders = typeof result === 'object' ? result.deletedOrders ?? 0 : 0
        toast.success(`${shop_domain} disconnected and ${deletedOrders} order(s) deleted`)
      } else {
        toast.success(`${shop_domain} disconnected`)
      }
    }
  }

  const handleStartEdit = async (integration: ShopifyIntegrationRecord) => {
    const targetId = integration.id
    setEditingId(targetId)
    setEditFormData({
      shop_domain: integration.shop_domain || '',
      uses_webhooks: !!integration.uses_webhooks,
      uses_api: !!integration.uses_api,
      webhook_secret: integration.webhook_secret || '',
      api_access_token: '',
    })
    setShowEditToken(false)

    setEditLoadingId(targetId)
    const details = await fetchIntegrationDetails(targetId)
    setEditLoadingId((current) => (current === targetId ? null : current))

    if (details && editingIdRef.current === targetId) {
      setEditFormData({
        shop_domain: details.shop_domain || '',
        uses_webhooks: !!details.uses_webhooks,
        uses_api: !!details.uses_api,
        webhook_secret: details.webhook_secret || '',
        api_access_token: details.api_access_token || '',
      })
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditLoadingId(null)
    setEditFormData({
      shop_domain: '',
      uses_webhooks: false,
      uses_api: false,
      webhook_secret: '',
      api_access_token: '',
    })
    setShowEditToken(false)
    clearError()
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingId) return

    const payload: Record<string, unknown> = {
      shop_domain: sanitizeDomain(editFormData.shop_domain),
      uses_webhooks: editFormData.uses_webhooks,
      uses_api: editFormData.uses_api,
    }

    const webhookSecret = editFormData.webhook_secret.trim()
    if (editFormData.uses_webhooks && webhookSecret.length > 0) {
      payload.webhook_secret = webhookSecret
    }

    const apiToken = editFormData.api_access_token.trim()
    if (editFormData.uses_api && apiToken.length > 0) {
      payload.api_access_token = apiToken
    }

    const success = await updateIntegration(editingId, payload)
    if (success) {
      handleCancelEdit()
    }
  }

  /** Integration was soft-disconnected: token revoked, app uninstalled, data preserved */
  const isDisconnected = (integration: ShopifyIntegrationRecord) =>
    !!integration.disconnected_at

  const getStatusBadge = (integration: ShopifyIntegrationRecord) => {
    if (isDisconnected(integration)) {
      return <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700">Disconnected</Badge>
    }
    if (!integration.is_active) return <Badge variant="secondary">Disabled</Badge>
    return <Badge className="bg-green-100 text-green-800">Connected</Badge>
  }

  return (
    <>
    <IntegrationCardLayout
      title="Shopify"
      description="Connect your Shopify stores to enable order processing and webhook handling."
      icon={<ShoppingBag className="h-5 w-5" />}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              {error.message}
              <Button size="sm" variant="outline" onClick={() => { clearError(); refresh(); }} className="mt-2">
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Connected Integrations */}
        <div className="space-y-2">
          {isLoading ? (
            <div className="animate-pulse h-20 bg-gray-200 rounded w-full"></div>
          ) : integrations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No Shopify integrations connected yet.</p>
          ) : (
            integrations.map((integration) => (
              <div key={integration.id} className="p-4 border rounded-md bg-white space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <ShoppingBag className="w-4 h-4 text-gray-500 mt-1" />
                    <div className="text-sm flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium break-words">{integration.shop_domain}</span>
                        {editingId === integration.id && (
                          <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                            Editing
                          </Badge>
                        )}
                        {billingControlsVisible && integration.is_billing_store && integration.billing_eligible && (
                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                            <CreditCard className="w-3 h-3 mr-1" />
                            <span>Billing store</span>
                          </Badge>
                        )}
                        {billingChannel === BILLING_CHANNELS.SHOPIFY && integration.is_billing_store && integration.billing_eligible === false && (
                          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                            <CreditCard className="w-3 h-3 mr-1" />
                            <span>Billing store · unavailable</span>
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 flex items-center gap-2">
                        {integration.auth_method === 'oauth' && integration.custom_app_client_id ? (
                          <Badge variant="outline" className="text-xs border-purple-200 text-purple-700">Custom App</Badge>
                        ) : integration.auth_method === 'oauth' ? (
                          <Badge variant="outline" className="text-xs border-green-200 text-green-700">OAuth</Badge>
                        ) : (
                          <>
                            <Badge variant="outline" className="text-xs border-gray-200 text-gray-500">Legacy</Badge>
                            {integration.uses_webhooks && <span>Webhooks</span>}
                            {integration.uses_api && <span>API</span>}
                          </>
                        )}
                        {getStatusBadge(integration)}
                      </div>
                      {/* Custom app credentials summary */}
                      {integration.custom_app_client_id && (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <div><span>Client ID:</span> <code className="bg-gray-100 px-1 rounded">{integration.custom_app_client_id}</code></div>
                          {integration.custom_app_secret_hint && (
                            <div><span>Secret:</span> <code className="bg-gray-100 px-1 rounded">{integration.custom_app_secret_hint}</code></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {isDisconnected(integration) ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Disconnected state: Reconnect + Delete Data */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-700 hover:bg-blue-50"
                          disabled={isConnecting}
                          onClick={() => startOAuthConnect(integration.shop_domain)}
                        >
                          {isConnecting ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4 mr-1" />
                          )}
                          <span>Reconnect</span>
                        </Button>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-700 hover:bg-red-50"
                                  onClick={() => setDisconnectDialogIntegration(integration)}
                                  disabled={!canDeleteData || disconnectingId === integration.id}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  {disconnectingId === integration.id ? 'Deleting...' : 'Delete Data'}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!canDeleteData && (
                              <TooltipContent>
                                <p>Requires &quot;integrations.shopify.delete_data&quot; permission</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : (
                      <>
                        {/* Active / Disabled state: toggle on top, action buttons below */}
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`shopify-toggle-${integration.id}`} className="text-xs text-gray-600">
                            {integration.is_active ? 'Enabled' : 'Disabled'}
                          </Label>
                          <Switch
                            id={`shopify-toggle-${integration.id}`}
                            checked={integration.is_active}
                            onCheckedChange={() => toggleIntegration(integration.id, !integration.is_active)}
                            disabled={togglingId === integration.id}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                        {/* TODO: enable upgrade button once public Shopify app is published */}
                        {integration.auth_method === 'oauth' && integration.is_active && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-amber-200 text-amber-700 hover:bg-amber-50"
                            disabled={isConnecting}
                            onClick={() => startOAuthConnect(integration.shop_domain)}
                          >
                            {isConnecting ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-1" />
                            )}
                            <span>Reinstall App</span>
                          </Button>
                        )}
                        {integration.is_active && integration.uses_api && (
                          <Popover
                            open={syncPopoverOpen === integration.id}
                            onOpenChange={(open) => {
                              setSyncPopoverOpen(open ? integration.id : null)
                              if (open) {
                                const d = new Date(); d.setMonth(d.getMonth() - 2); setSyncDate(d)
                              }
                            }}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={syncingId === integration.id}
                              >
                                {syncingId === integration.id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                )}
                                <span>{syncingId === integration.id ? 'Syncing...' : 'Sync Orders'}</span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-3" align="end">
                              <div className="space-y-3">
                                <div className="space-y-1">
                                  <Label className="text-xs font-medium">Sync orders since</Label>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <CalendarIcon className="w-3.5 h-3.5" />
                                    {syncDate.toLocaleDateString()}
                                  </div>
                                </div>
                                <Calendar
                                  mode="single"
                                  selected={syncDate}
                                  onSelect={(date) => date && setSyncDate(date)}
                                  disabled={(date) => {
                                    const maxLookback = new Date(); maxLookback.setMonth(maxLookback.getMonth() - 6)
                                    return date > new Date() || date < maxLookback
                                  }}
                                  initialFocus
                                />
                                <Button
                                  size="sm"
                                  className="w-full"
                                  disabled={syncingId === integration.id}
                                  onClick={async () => {
                                    setSyncPopoverOpen(null)
                                    const success = await syncOrders(integration.id, syncDate.toISOString())
                                    if (success) {
                                      toast.success('Order sync started — results will appear shortly')
                                    }
                                  }}
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  <span>Start Sync</span>
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        {integration.is_active && integration.uses_api && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={syncingCatalogId === integration.id}
                            onClick={async () => {
                              const result = await syncCatalog(integration.id)
                              if (result.ok) {
                                toast.success(
                                  result.status === 202
                                    ? 'Catalog sync started — products will update shortly'
                                    : 'Catalog sync already in progress',
                                )
                              }
                            }}
                          >
                            {syncingCatalogId === integration.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-1" />
                            )}
                            <span>{syncingCatalogId === integration.id ? 'Syncing...' : 'Sync Catalog'}</span>
                          </Button>
                        )}
                        {integration.auth_method === 'legacy' && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => handleStartEdit(integration)}
                            disabled={updatingId === integration.id}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            <span>Edit</span>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDisconnectDialogIntegration(integration)}
                          disabled={disconnectingId === integration.id || (updatingId === integration.id)}
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          <span>{disconnectingId === integration.id ? 'Disconnecting...' : 'Disconnect'}</span>
                        </Button>
                        </div>
                        {/* Rare, deliberate action — a quiet link, not a peer of the daily buttons */}
                        {billingControlsVisible && integration.billing_eligible && !integration.is_billing_store && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-700 hover:underline disabled:opacity-50"
                                  disabled={settingBillingStoreId === integration.id}
                                  onClick={() => setBillingStore(integration.id)}
                                >
                                  {settingBillingStoreId === integration.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CreditCard className="h-3 w-3" />
                                  )}
                                  <span>Set as billing store</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>
                                  The billing store is the Shopify store your Versafy plan is charged
                                  through. Click to move billing to this store.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Edit Form */}
                {editingId === integration.id && (
                  <form onSubmit={handleUpdate} className="space-y-4 rounded-md border border-blue-100 bg-blue-50/50 p-4">
                    {editLoadingId === integration.id && (
                      <div className="flex items-center gap-2 text-xs text-blue-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading saved credentials...</span>
                      </div>
                    )}
                    <div>
                      <Label htmlFor={`shopify-edit-domain-${integration.id}`}>Shop Domain *</Label>
                      <Input
                        id={`shopify-edit-domain-${integration.id}`}
                        value={editFormData.shop_domain}
                        onChange={(e) => setEditFormData({ ...editFormData, shop_domain: e.target.value })}
                        placeholder="your-store.myshopify.com"
                        required
                        disabled={editLoadingId === integration.id}
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`shopify-edit-webhooks-${integration.id}`}
                          checked={editFormData.uses_webhooks}
                          onCheckedChange={(checked) =>
                            setEditFormData({ ...editFormData, uses_webhooks: checked as boolean })
                          }
                          disabled={editLoadingId === integration.id}
                        />
                        <Label htmlFor={`shopify-edit-webhooks-${integration.id}`}>Enable Webhooks</Label>
                      </div>

                      {editFormData.uses_webhooks && (
                        <div>
                          <Label htmlFor={`shopify-edit-webhook-secret-${integration.id}`}>
                            <span>Webhook Secret</span> <span>{integration.webhook_secret ? '(optional)' : '*'}</span>
                          </Label>
                          <Input
                            id={`shopify-edit-webhook-secret-${integration.id}`}
                            value={editFormData.webhook_secret}
                            onChange={(e) => setEditFormData({ ...editFormData, webhook_secret: e.target.value })}
                            placeholder="New webhook secret"
                            disabled={editLoadingId === integration.id}
                          />
                          <p className="text-xs text-gray-500 mt-1">Leave blank to keep your current webhook secret.</p>
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`shopify-edit-api-${integration.id}`}
                          checked={editFormData.uses_api}
                          onCheckedChange={(checked) =>
                            setEditFormData({ ...editFormData, uses_api: checked as boolean })
                          }
                          disabled={editLoadingId === integration.id}
                        />
                        <Label htmlFor={`shopify-edit-api-${integration.id}`}>Enable API Access</Label>
                      </div>

                      {editFormData.uses_api && (
                        <div>
                          <Label htmlFor={`shopify-edit-api-token-${integration.id}`}>
                            <span>Admin API Access Token</span> <span>{integration.api_access_token_enc ? '(optional)' : '*'}</span>
                          </Label>
                          <div className="relative">
                            <Input
                              id={`shopify-edit-api-token-${integration.id}`}
                              type={showEditToken ? 'text' : 'password'}
                              value={editFormData.api_access_token}
                              onChange={(e) => setEditFormData({ ...editFormData, api_access_token: e.target.value })}
                              placeholder="New Shopify Admin API access token"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3"
                              onClick={() => setShowEditToken(!showEditToken)}
                              disabled={editLoadingId === integration.id}
                            >
                              {showEditToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Leave blank to keep your existing Admin API token.</p>
                        </div>
                      )}
                    </div>

                    <Alert className="border-blue-200 bg-blue-50 text-blue-900">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5" />
                        <AlertDescription className="text-xs">
                          Turning off Webhooks or API access removes the stored secrets. Enter new values only when you need to rotate them.
                        </AlertDescription>
                      </div>
                    </Alert>

                    <div className="flex items-center gap-2">
                      <Button type="submit" disabled={updatingId === integration.id}>
                        {updatingId === integration.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            <span key="saving">Saving...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            <span key="save-changes">Save Changes</span>
                          </>
                        )}
                      </Button>
                     <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={updatingId === integration.id}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            ))
          )}
        </div>

        {/* Connect Button. Shopify channel installs from the App Store (2.3.1 — no
            manual domain entry); Stripe/manual tenants connect via their own custom app. */}
        {billingChannel === BILLING_CHANNELS.SHOPIFY ? (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              disabled={!shopifyListingUrl}
              onClick={() => {
                if (shopifyListingUrl) window.location.href = shopifyListingUrl
              }}
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              <span>Connect Shopify Store</span>
            </Button>
            <p className="text-xs text-gray-500">
              {shopifyListingUrl
                ? 'Install the Versafy app from the Shopify App Store, then add the store to this account.'
                : 'Available once the Versafy app is published on the Shopify App Store.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button onClick={() => setShowConnectDialog(true)}>
              <LinkIcon className="w-4 h-4 mr-2" />
              <span>Connect Shopify Store</span>
            </Button>
            <p className="text-xs text-gray-500">Connect using your own Shopify app.</p>
          </div>
        )}
      </div>

      {/* Disconnect / Delete Data Confirmation Dialog */}
      <AlertDialog
        open={!!disconnectDialogIntegration}
        onOpenChange={(open) => { if (!open) { setDisconnectDialogIntegration(null); setDeleteConfirmInput('') } }}
      >
        <AlertDialogContent>
          {disconnectDialogIntegration && isDisconnected(disconnectDialogIntegration) ? (
            <>
              {/* Already disconnected — only Delete Data option */}
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <span>Delete data for</span> {disconnectDialogIntegration.shop_domain}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This store is already disconnected. This will permanently delete all orders, attribution, sync logs, and channel data associated with this integration.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label htmlFor="delete-confirm-input" className="text-sm text-muted-foreground">
                  <span>Type</span> <span className="font-mono font-semibold text-foreground">{disconnectDialogIntegration.shop_domain}</span> <span>to confirm</span>
                </Label>
                <Input
                  id="delete-confirm-input"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder={disconnectDialogIntegration.shop_domain}
                  autoComplete="off"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={!!disconnectingId}>
                  Cancel
                </AlertDialogCancel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="destructive"
                          disabled={!canDeleteData || !!disconnectingId || deleteConfirmInput !== disconnectDialogIntegration.shop_domain}
                          onClick={() => handleDisconnect('hard')}
                        >
                          {disconnectingId ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete All Data
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canDeleteData && (
                      <TooltipContent>
                        <p>Requires &quot;integrations.shopify.delete_data&quot; permission</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              {/* Active integration — Disconnect / Disconnect & Delete */}
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <span>Disconnect</span> {disconnectDialogIntegration?.shop_domain}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will revoke the access token and uninstall the app from the Shopify store. Choose how to handle existing data:
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label htmlFor="delete-confirm-input" className="text-sm text-muted-foreground">
                  <span>Type</span> <span className="font-mono font-semibold text-foreground">{disconnectDialogIntegration?.shop_domain}</span> <span>to enable data deletion</span>
                </Label>
                <Input
                  id="delete-confirm-input"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder={disconnectDialogIntegration?.shop_domain}
                  disabled={!canDeleteData}
                  autoComplete="off"
                />
              </div>
              <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
                <AlertDialogCancel disabled={!!disconnectingId}>
                  Cancel
                </AlertDialogCancel>
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  disabled={!!disconnectingId}
                  onClick={() => handleDisconnect('soft')}
                >
                  {disconnectingId ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4 mr-2" />
                  )}
                  <span>Disconnect</span>
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="destructive"
                          disabled={!canDeleteData || !!disconnectingId || deleteConfirmInput !== disconnectDialogIntegration?.shop_domain}
                          onClick={() => handleDisconnect('hard')}
                        >
                          {disconnectingId ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Disconnect &amp; Delete Data
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canDeleteData && (
                      <TooltipContent>
                        <p>Requires &quot;integrations.shopify.delete_data&quot; permission</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </IntegrationCardLayout>

    <AddShopifyIntegrationDialog
      open={showConnectDialog}
      onOpenChange={setShowConnectDialog}
      onCustomAppConnect={startCustomAppConnect}
      onLegacyConnect={connectIntegration}
      isConnecting={isConnecting}
      error={error}
      onClearError={clearError}
      requiredScopes={requiredScopes}
    />
    </>
  )
}
